import React, { createContext, useContext, useState, useEffect } from "react";
import { login as loginService, logout as logoutService, validateToken } from "../services/authService";
import { validateAzureToken } from "../services/azureAuthService";
import * as notificationsService from "../services/notificationsService";
import { msalInstance } from "../config/msalConfig";
import axios from "axios";

/**
 * AuthContext - Contexto de autenticación
 *
 * Centraliza el estado de autenticación de la aplicación,
 * evitando acceso directo a localStorage en múltiples componentes
 */
const AuthContext = createContext(null);

/**
 * Hook personalizado para acceder al contexto de autenticación
 *
 * @returns {Object} { user, token, isAuthenticated, login, logout, loading }
 * @throws {Error} Si se usa fuera de un AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe ser usado dentro de un AuthProvider");
  }
  return context;
};

/**
 * AuthProvider - Proveedor del contexto de autenticación
 *
 * Maneja:
 * - Estado de autenticación (token, usuario)
 * - Login/Logout
 * - Persistencia en localStorage
 * - Estado de carga inicial
 */
export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Cargar y validar token desde localStorage al montar el componente
  useEffect(() => {
    const initializeAuth = async () => {
      // Primero, inicializar MSAL (requerido en v3+)
      try {
        await msalInstance.initialize();
        console.log("MSAL inicializado correctamente");
      } catch (error) {
        console.error("Error inicializando MSAL:", error);
      }

      // Luego, manejar el redirect de Azure AD si existe
      try {
        const response = await msalInstance.handleRedirectPromise();
        if (response && response.account) {
          console.log("Azure AD redirect detectado, procesando...");
          // Usuario volvió de Azure AD, procesar el login
          try {
            const data = await validateAzureToken(response.accessToken, response.account);

            // Guardar datos del usuario con información completa incluyendo rol de la BD
            const userData = {
              authenticated: true,
              id: data.user.id,
              username: data.user.username,
              email: data.user.email,
              fullName: data.user.fullName,
              name: data.user.name,
              roleId: data.user.roleId,
              roleName: data.user.roleName,
              permissions: data.user.permissions || [],
              customPermissions: data.user.customPermissions || [],
              authProvider: "azureAd"
            };

            // IMPORTANTE: Guardar primero en localStorage ANTES de actualizar el estado
            // Esto asegura que si hay un re-render, los datos ya estén disponibles
            localStorage.setItem("token", data.token);
            localStorage.setItem("user", JSON.stringify(userData));

            console.log("✅ Usuario autenticado con Azure AD:", userData);

            // Actualizar estado después de guardar en localStorage
            setToken(data.token);
            setUser(userData);
            setLoading(false);

            // Forzar redirección al dashboard después del login de Azure
            // Usamos setTimeout para asegurar que el estado se actualice primero
            setTimeout(() => {
              window.location.href = "/";
            }, 100);

            return; // Salir temprano, ya autenticamos
          } catch (error) {
            console.error("❌ Error validando token de Azure AD:", error);
            // Si falla la validación, continuar con la autenticación normal
          }
        }
      } catch (error) {
        console.error("Error manejando redirect de Azure AD:", error);
      }

      // Si no hay redirect de Azure, continuar con autenticación normal
      const storedToken = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");

      if (storedToken) {
        // Validar que el token sea válido antes de autenticar
        const isValid = await validateToken(storedToken);

        if (isValid) {
          setToken(storedToken);
          // Cargar datos del usuario guardados
          if (storedUser) {
            try {
              setUser(JSON.parse(storedUser));
            } catch (e) {
              setUser({ authenticated: true });
            }
          } else {
            setUser({ authenticated: true });
          }
        } else {
          // Token inválido o expirado, limpiar todo
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          setToken(null);
          setUser(null);
        }
      }

      setLoading(false);
    };

    initializeAuth();

    // Listener para detectar cuando el interceptor expira la sesión
    const handleSessionExpired = () => {
      setToken(null);
      setUser(null);
    };

    window.addEventListener('session-expired', handleSessionExpired);

    return () => {
      window.removeEventListener('session-expired', handleSessionExpired);
    };
  }, []);

  /**
   * Función de login
   * @param {string} username - Nombre de usuario
   * @param {string} password - Contraseña
   * @returns {Promise<Object>} - Datos del usuario
   */
  const login = async (username, password) => {
    try {
      const data = await loginService(username, password);

      // Guardar token en estado y localStorage
      const accessToken = data.accessToken;
      setToken(accessToken);
      localStorage.setItem("token", accessToken);

      // Guardar datos del usuario con información completa
      const userData = {
        authenticated: true,
        id: data.id,  // Agregar ID del usuario para Socket.IO
        username: data.username,
        email: data.email,
        fullName: data.fullName,
        roleId: data.roleId,
        roleName: data.roleName,
        permissions: data.permissions || [],
        customPermissions: data.customPermissions || []
      };
      setUser(userData);
      localStorage.setItem("user", JSON.stringify(userData));

      return data;
    } catch (error) {
      throw error;
    }
  };

  /**
   * Función de login con Azure AD
   * @param {string} accessToken - Token de acceso de Azure AD
   * @param {Object} account - Información de la cuenta de Azure AD
   * @returns {Promise<Object>} - Datos del usuario
   */
  const loginWithAzure = async (accessToken, account) => {
    try {
      const data = await validateAzureToken(accessToken, account);

      // Guardar token en estado y localStorage
      setToken(data.token);
      localStorage.setItem("token", data.token);

      // NOTA: El refresh token se guarda en cookies httpOnly desde el backend,
      // no en localStorage (igual que el login normal)

      // Guardar datos del usuario con información completa incluyendo rol de la BD
      const userData = {
        authenticated: true,
        id: data.user.id,  // Agregar ID del usuario para Socket.IO
        username: data.user.username,
        email: data.user.email,
        fullName: data.user.fullName,
        name: data.user.name,
        roleId: data.user.roleId,
        roleName: data.user.roleName,
        permissions: data.user.permissions || [], // Permisos básicos
        customPermissions: data.user.customPermissions || [], // Permisos custom
        authProvider: "azureAd"
      };
      setUser(userData);
      localStorage.setItem("user", JSON.stringify(userData));

      return data;
    } catch (error) {
      throw error;
    }
  };

  /**
   * Función de logout
   */
  const logout = async () => {
    try {
      // Obtener token FCM del localStorage antes de limpiar todo
      const fcmToken = localStorage.getItem("fcmToken");

      // Intentar eliminar token FCM si existe
      if (fcmToken) {
        try {
          await notificationsService.deleteFCMToken(fcmToken);
          console.log("🧹 Token FCM eliminado al cerrar sesión");
          localStorage.removeItem("fcmToken");
        } catch (error) {
          console.error("Error eliminando token FCM:", error);
          // Continuar con logout aunque falle
        }
      }

      // Intentar logout en el backend
      await logoutService();
    } catch (error) {
      console.error("Error en logout:", error);
      // Continuar con el logout local incluso si falla el backend
    } finally {
      // Limpiar estado y localStorage
      setToken(null);
      setUser(null);
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("fcmToken");
    }
  };

  /**
   * Verificar si el usuario tiene permiso en un módulo
   * @param {string} moduleName - Nombre del módulo (ej: 'solicitudes', 'administracion.usuarios')
   * @param {string} permission - Tipo de permiso: 'view', 'create', 'edit', 'delete', 'approve'
   * @returns {boolean}
   */
  const hasPermission = (moduleName, permission = 'view') => {
    if (!user || !user.permissions) return false;

    const modulePermission = user.permissions.find(p => p.moduleName === moduleName);
    if (!modulePermission) return false;

    switch (permission) {
      case 'view':
        return modulePermission.canView;
      case 'create':
        return modulePermission.canCreate;
      case 'edit':
        return modulePermission.canEdit;
      case 'delete':
        return modulePermission.canDelete;
      case 'approve':
        return modulePermission.canApprove;
      default:
        return false;
    }
  };

  /**
   * Verificar si el usuario tiene acceso a cualquier submódulo de un módulo padre
   * @param {string} parentModuleName - Nombre del módulo padre (ej: 'solicitudes', 'intermediarios')
   * @returns {boolean}
   */
  const hasAccessToParentModule = (parentModuleName) => {
    if (!user || !user.permissions) return false;

    // Buscar cualquier permiso que comience con el nombre del módulo padre
    return user.permissions.some(p =>
      p.moduleName.startsWith(parentModuleName) && p.canView
    );
  };

  /**
   * Verificar si el usuario tiene un permiso custom en un módulo
   * @param {string} moduleName - Nombre del módulo (ej: 'solicitudes.alquileres', 'solicitudes.directores')
   * @param {string} actionKey - Clave de la acción custom (ej: 'aprobar', 'enviar', 'emitir', 'anular')
   * @returns {boolean}
   */
  const hasCustomPermission = (moduleName, actionKey) => {
    if (!user || !user.customPermissions) return false;

    const customPermission = user.customPermissions.find(
      cp => cp.moduleName === moduleName && cp.actionKey === actionKey
    );

    return customPermission ? customPermission.hasPermission : false;
  };

  /**
   * Valor del contexto
   */
  const value = {
    user,
    token,
    isAuthenticated: !!token,
    login,
    loginWithAzure,
    logout,
    loading,
    hasPermission,
    hasAccessToParentModule,
    hasCustomPermission
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
